import { type ReactNode, useContext, useMemo } from 'react';
import { type Breakpoint, Col, type ColProps } from 'antd';
import isEqual from 'lodash/isEqual';
import { PosGridContext } from 'pos-client/contexts/PosGridContext.tsx';

/** Ant-d column responsive size fields */
export type PosColumnResponsiveProps = Pick<
  ColProps,
  'span' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
>;

/**
 * Props for {@link PosColumn}
 */
export interface PosColumnProps extends Omit<ColProps, 'children'> {
  children: ReactNode;
}

/**
 * @component
 * @description Responsive column to be used as a child of `PosGrid`
 *
 * @see {@link https://ant.design/components/grid | AntD Grid Component}
 * @param props {@link PosColumnProps}
 * @remarks This component should not be used outside of `<PosGrid>`
 * @example
 * ```tsx
 * <PosGrid>
    <PosColumn span={12}>
     <PosCard
       headerProps={{title: 'Example Card'}}
     />
    </PosColumn>
    <PosColumn span={12}>
     <PosCard
       headerProps={{title: 'Example Card 2'}}
     />
    </PosColumn>
   </PosGrid>
 * ```
 */
export const PosColumn = ({
  children,
  span,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  ...props
}: PosColumnProps) => {
  const { responsive, breakpointsLess, currentBreakpoint } =
    useContext(PosGridContext);

  const responsiveProps = useMemo(
    () => ({
      span,
      xs,
      sm,
      md,
      lg,
      xl,
      xxl,
    }),
    [lg, md, sm, span, xl, xs, xxl]
  );

  const columnPropsWidthAdjusted = useMemo(() => {
    const validBreakpoints = Object.entries(breakpointsLess)
      .filter(
        (value) => !!value[1] && !!responsiveProps[value[0] as Breakpoint]
      )
      .map((value) => value[0]) as Breakpoint[];

    const nextBreakpoint = validBreakpoints.findLast(
      (bp) => typeof responsiveProps[bp] === 'number'
    );

    const currentBreakpointValue =
      typeof responsiveProps[currentBreakpoint] === 'number'
        ? responsiveProps[currentBreakpoint]
        : undefined;
    const nextBreakpointValue =
      nextBreakpoint && typeof responsiveProps[nextBreakpoint] === 'number'
        ? responsiveProps[nextBreakpoint]
        : undefined;

    const responsiveSpan = currentBreakpointValue ?? nextBreakpointValue ?? 24;

    const emptyResponsive = {
      span: undefined,
      xs: undefined,
      sm: undefined,
      md: undefined,
      lg: undefined,
      xl: undefined,
      xxl: undefined,
    };

    const adjustedProps: ColProps = {
      ...props,
      ...responsiveProps,
      ...(!isEqual(responsiveProps, emptyResponsive) &&
        responsive === 'container' && {
          ...emptyResponsive,
          span: responsiveSpan,
        }),
    };

    return adjustedProps;
  }, [breakpointsLess, currentBreakpoint, props, responsive, responsiveProps]);

  return <Col {...columnPropsWidthAdjusted}>{children}</Col>;
};
